<!-- Cell templates -->
<ng-template #categoriesTpl let-row>
  <div class="flex flex-wrap gap-1">
    @for (cat of row.categories; track cat) {
      <span
        class="px-2 py-0.5 rounded-full text-xs bg-surface-200 text-slate-600 font-medium whitespace-nowrap"
        >{{ cat }}</span
      >
    } @empty {
      <span class="text-slate-400 text-sm">—</span>
    }
  </div>
</ng-template>

<ng-template #peopleTpl let-row>
  @if (row.people.length) {
    <div class="flex items-center gap-2">
      <img src="/icons/persons-icon.svg" class="w-4 h-4 shrink-0" alt="" />
      <span class="text-slate-600 text-xs">{{ row.people.join(', ') }}</span>
    </div>
  } @else {
    <span class="text-slate-400 text-sm">—</span>
  }
</ng-template>

<ng-template #statusTpl let-row>
  <span
    class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
    [style.background]="row.status.background"
    [style.color]="row.status.color"
    >{{ row.status.label }}</span
  >
</ng-template>

<ng-template #actionsTpl>
  <div class="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3">
    <button
      type="button"
      (click)="clearCriteria()"
      class="px-5 py-2.5 text-sm font-semibold text-slate-500 hover:text-navy-900 transition-colors cursor-pointer"
    >
      Clear
    </button>
    <button
      type="button"
      (click)="runSearch()"
      [disabled]="loading() || recordTypes().length === 0"
      class="flex items-center justify-center gap-2 whitespace-nowrap px-6 py-2.5 text-sm font-semibold text-white bg-accent-500 hover:opacity-90 rounded-xl transition-opacity cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <img src="/icons/search-icon.svg" class="w-4 h-4 brightness-0 invert" alt="" />
      {{ loading() ? 'Searching…' : 'Run search' }}
    </button>
  </div>
</ng-template>

<!-- Page layout -->
<div class="flex flex-col h-full overflow-y-auto bg-surface-100 px-4 sm:px-10 py-6 gap-4">
  <!-- Criteria -->
  <div class="bg-white rounded-2xl border border-slate-100 p-4 sm:p-6">
    <!-- One grid: mode, record type, program and categories on the first row; dates,
         person and the actions on the second -->
    <div class="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-x-6 gap-y-5 items-start">
      <div class="flex flex-col gap-1.5 min-w-0">
        <p class="text-xs font-semibold text-navy-600 tracking-wider">Find records via</p>
        <div class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1 w-full sm:w-fit">
          <button
            type="button"
            (click)="setMode('custom')"
            [ngClass]="
              mode() === 'custom'
                ? 'bg-white text-navy-900 shadow-sm'
                : 'text-slate-500 hover:text-navy-700'
            "
            class="flex-1 sm:flex-none px-5 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
          >
            Custom Search
          </button>
          <button
            type="button"
            (click)="setMode('predefined')"
            [ngClass]="
              mode() === 'predefined'
                ? 'bg-white text-navy-900 shadow-sm'
                : 'text-slate-500 hover:text-navy-700'
            "
            class="flex-1 sm:flex-none px-5 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
          >
            Predefined List
          </button>
        </div>
      </div>
      @if (mode() === 'custom') {
        @if (recordTypes().length === 0) {
          <p class="text-sm text-slate-500 self-end 2xl:col-span-3">
            You don't have access to incidents or investigations.
          </p>
        } @else {
          <!-- Record type -->
          <div class="flex flex-col gap-1.5 min-w-0">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">
              Type of record <span class="text-red-500">*</span>
            </label>
            <div
              class="flex items-center bg-slate-100 rounded-3xl px-1 py-1 gap-1 w-full sm:w-fit"
              [ngClass]="
                submitAttempted() && missing().recordType
                  ? 'ring-2 ring-red-100 border border-red-400'
                  : ''
              "
            >
              @for (t of recordTypes(); track t.id) {
                <button
                  type="button"
                  (click)="setRecordType(t.id)"
                  [ngClass]="
                    recordType() === t.id
                      ? 'bg-white text-navy-900 shadow-sm'
                      : 'text-slate-500 hover:text-navy-700'
                  "
                  class="flex-1 sm:flex-none px-5 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer"
                >
                  {{ t.label }}
                </button>
              }
            </div>
            @if (submitAttempted() && missing().recordType) {
              <p class="text-xs text-red-500">Choose incidents or investigations</p>
            }
          </div>

          <!-- Program -->
          <div class="flex flex-col gap-1.5 min-w-0">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Program</label>
            @if (recordType()) {
              <app-multiselect-dropdown
                [options]="programOptions()"
                [selected]="programIds()"
                placeholder="All programs"
                (selectedChange)="programIds.set($event)"
              />
            } @else {
              <p class="h-11 flex items-center text-xs text-slate-400">
                Choose a type of record first
              </p>
            }
          </div>

          <!-- Categories -->
          <div class="flex flex-col gap-1.5 min-w-0">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">Categories</label>
            <app-multiselect-dropdown
              [options]="categoryOptions()"
              [selected]="categoryIds()"
              placeholder="All categories"
              (selectedChange)="categoryIds.set($event)"
            />
          </div>

          <!-- Date range: picker, quick ranges below -->
          <div class="flex flex-col gap-1.5 min-w-0 2xl:col-span-2">
            <label class="text-xs font-semibold text-navy-600 tracking-wider">
              Date range <span class="text-red-500">*</span>
            </label>
            <app-date-range-picker
              [dateFrom]="dateFrom()"
              [dateTo]="dateTo()"
              placeholder="Or pick a start and end date"
              [error]="submitAttempted() && missing().dates"
              (rangeChange)="onRangeChange($event)"
            />
            <div class="flex flex-wrap gap-1.5">
              @for (p of datePresets; track p.id) {
                <button
                  type="button"
                  (click)="applyPreset(p.id)"
                  [ngClass]="
                    activePreset() === p.id
                      ? 'border-accent-400 text-accent-500 bg-accent-50'
                      : 'border-surface-300 text-navy-700 bg-white hover:border-accent-400 hover:text-accent-500'
                  "
                  class="px-3 py-1 text-xs font-semibold border rounded-full transition-colors cursor-pointer"
                >
                  {{ p.label }}
                </button>
              }
            </div>
            @if (submitAttempted() && missing().dates) {
              <p class="text-xs text-red-500">Choose a start and an end date</p>
            }
          </div>

          <!-- Person involved -->
          <div class="flex flex-col gap-1.5 min-w-0">
            <div class="flex items-center justify-between">
              <label class="text-xs font-semibold text-navy-600 tracking-wider"
                >Person involved</label
              >
              @if (personId()) {
                <button
                  type="button"
                  (click)="personId.set('')"
                  class="text-2xs font-semibold text-accent-500 hover:text-accent-600 cursor-pointer"
                >
                  Clear
                </button>
              }
            </div>
            <app-person-select
              [value]="personId()"
              placeholder="Anyone"
              (changed)="personId.set($event)"
            />
          </div>

          <div class="self-end lg:col-span-2 2xl:col-span-1">
            <ng-container [ngTemplateOutlet]="actionsTpl" />
          </div>
        }
      }

      @if (mode() === 'predefined') {
        <div class="flex flex-col gap-1.5 min-w-0 2xl:col-span-2">
          <label class="text-xs font-semibold text-navy-600 tracking-wider">
            List <span class="text-red-500">*</span>
          </label>
          @if (predefinedListsLoaded() && predefinedListOptions().length === 0) {
            <p class="h-11 flex items-center text-xs text-slate-400">
              There are no predefined lists available.
            </p>
          } @else {
            <app-select
              placeholder="Choose a list"
              [options]="predefinedListOptions()"
              [value]="predefinedListId()"
              [loading]="!predefinedListsLoaded()"
              [error]="submitAttempted() && missing().predefinedList"
              (changed)="predefinedListId.set($event)"
            />
            @if (submitAttempted() && missing().predefinedList) {
              <p class="text-xs text-red-500">Choose a list</p>
            }
          }
        </div>
        <div class="self-end lg:col-span-2 2xl:col-span-1">
          <ng-container [ngTemplateOutlet]="actionsTpl" />
        </div>
      }
    </div>
  </div>

  <!-- Results -->
  <div
    class="flex-1 min-h-80 bg-white rounded-xl border border-slate-100 overflow-hidden flex flex-col"
  >
    <div class="flex items-center justify-between px-4 sm:px-6 pt-4 sm:pt-5">
      <p class="text-sm font-bold text-navy-600">Results</p>
      @if (hasSearched() && !errorMessage()) {
        <p class="text-xs text-slate-500">
          {{ total() }} {{ total() === 1 ? 'record' : 'records' }}
        </p>
      }
    </div>

    @if (!hasSearched()) {
      <div class="flex-1 flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
        <img src="/icons/search-icon.svg" class="w-8 h-8 opacity-40" alt="" />
        <p class="text-sm text-slate-500">Set the criteria and run the search to see results.</p>
      </div>
    } @else if (errorMessage()) {
      <div class="flex-1 flex items-center justify-center px-6 py-12">
        <p class="text-sm text-red-500 text-center">{{ errorMessage() }}</p>
      </div>
    } @else {
      <app-data-table
        [columns]="columns()"
        [rows]="rows()"
        [pageSize]="pageSize"
        [total]="total()"
        [page]="page()"
        [loading]="loading()"
        [clickableRows]="true"
        (rowAction)="openRecord($event)"
        (pageChange)="onPageChange($event)"
      />
    }
  </div>
</div>
